import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { TappyLabsBranding } from '../TappyLabsBranding';
import Branding from '../Branding';
import ToastNotification from '../ToastNotification';

const TokenWalletModal = ({ isOpen, onClose, user, userTier, hasFeatureAccess }) => {
  const [activeTab, setActiveTab] = useState('wallet');
  const [tokenData, setTokenData] = useState({
    balance: 0,
    history: [],
    pendingGifts: [],
    streakDays: 0,
    profileTheme: null,
    usernameColor: null,
    profileBorder: null,
    profileEffect: null
  });
  const [bounties, setBounties] = useState([]);
  const [loading, setLoading] = useState(true);

  // Inject CSS animations
  useEffect(() => {
    const styleId = 'token-cosmetics-animations';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes flame-flicker {
          0% { transform: translateY(0) scaleY(1) scaleX(1); opacity: 0.6; }
          25% { transform: translateY(-8px) scaleY(1.3) scaleX(0.9); opacity: 0.9; }
          50% { transform: translateY(-15px) scaleY(1.5) scaleX(0.8); opacity: 1; }
          75% { transform: translateY(-8px) scaleY(1.2) scaleX(0.95); opacity: 0.85; }
          100% { transform: translateY(0) scaleY(1) scaleX(1); opacity: 0.6; }
        }
        @keyframes lightning-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.6), 0 0 40px rgba(139, 92, 246, 0.4); }
          50% { box-shadow: 0 0 30px rgba(139, 92, 246, 0.9), 0 0 60px rgba(139, 92, 246, 0.6), 0 0 80px rgba(139, 92, 246, 0.3); }
        }
        @keyframes dragon-orbit {
          0% { transform: translate(-50%, -50%) rotate(0deg) translateX(80px) rotate(0deg); }
          100% { transform: translate(-50%, -50%) rotate(360deg) translateX(80px) rotate(-360deg); }
        }
        @keyframes dragon-pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchTokenData();
      fetchBounties();
    }
  }, [isOpen]);

  // Make refresh function globally available when modal is open
  useEffect(() => {
    if (isOpen) {
      window.refreshTokenWallet = () => {
        fetchTokenData();
        fetchBounties();
      };
    }
    
    return () => {
      window.refreshTokenWallet = null;
    };
  }, [isOpen]);

  const fetchTokenData = async () => {
    try {
      const [balanceRes, historyRes, giftsRes] = await Promise.all([
        axios.get('/api/tokens/balance'),
        axios.get('/api/tokens/history'),
        axios.get('/api/tokens/pending-gifts')
      ]);

      setTokenData({
        balance: balanceRes.data.balance || 0,
        history: historyRes.data.transactions || [],
        pendingGifts: giftsRes.data.pending_gifts || [],
        streakDays: balanceRes.data.streak_days || 0,
        // Profile cosmetics
        profileTheme: balanceRes.data.profile_theme,
        usernameColor: balanceRes.data.username_color,
        profileBorder: balanceRes.data.profile_border,
        profileEffect: balanceRes.data.profile_effect,
        equippedProfileTheme: balanceRes.data.equipped_profile_theme,
        equippedProfileBorder: balanceRes.data.equipped_profile_border,
        equippedProfileEffect: balanceRes.data.equipped_profile_effect,
        // Mira customization
        miraOrbColor: balanceRes.data.mira_orb_color || 'default',
        miraGlowEffect: balanceRes.data.mira_glow_effect || 'default',
        miraEyeStyle: balanceRes.data.mira_eye_style || 'default',
        // Streak protection inventory
        streakFreezesOwned: balanceRes.data.streak_freezes_owned || 0,
        weekendAmuletActive: balanceRes.data.weekend_amulet_active || false
      });
    } catch (error) {
      console.error('Error fetching token data:', error);
    }
  };

  // Handle equipping/unequipping items (Minecraft-style)
  const handleEquipItem = async (itemType, value, action) => {
    try {
      const response = await axios.post('/api/tokens/equip', {
        item_type: itemType,
        value: value,
        action: action
      });

      if (response.data.success) {
        // Refresh token data to get updated equipped items
        await fetchTokenData();
      }
    } catch (error) {
      console.error('Error equipping item:', error);
      alert(error.response?.data?.error || 'Failed to equip item');
    }
  };

  const fetchBounties = async () => {
    try {
      const response = await axios.get('/api/bounties/available?limit=5');
      setBounties(response.data.bounties || []);
    } catch (error) {
      console.error('Error fetching bounties:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      <style>{`
        /* Remove number input spinners */
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        input[type=number] {
          -moz-appearance: textfield;
        }

        /* Hide scrollbar for token wallet tabs */
        .token-wallet-tabs::-webkit-scrollbar {
          display: none;
        }

        .fire-flame {
          animation: flame-flicker 1s ease-in-out infinite !important;
        }
        .fire-flame:nth-child(1) { animation-duration: 0.8s !important; animation-delay: 0s !important; }
        .fire-flame:nth-child(2) { animation-duration: 1.1s !important; animation-delay: 0.1s !important; }
        .fire-flame:nth-child(3) { animation-duration: 0.9s !important; animation-delay: 0.2s !important; }
        .fire-flame:nth-child(4) { animation-duration: 1.2s !important; animation-delay: 0.3s !important; }
        .fire-flame:nth-child(5) { animation-duration: 0.7s !important; animation-delay: 0.4s !important; }
        .fire-flame:nth-child(6) { animation-duration: 1s !important; animation-delay: 0.5s !important; }
        .fire-flame:nth-child(7) { animation-duration: 1.3s !important; animation-delay: 0.6s !important; }
        .fire-flame:nth-child(8) { animation-duration: 0.85s !important; animation-delay: 0.7s !important; }

        @keyframes flame-flicker {
          0% { transform: translateY(0) scaleY(1) scaleX(1); opacity: 0.6; }
          25% { transform: translateY(-8px) scaleY(1.3) scaleX(0.9); opacity: 0.9; }
          50% { transform: translateY(-15px) scaleY(1.5) scaleX(0.8); opacity: 1; }
          75% { transform: translateY(-8px) scaleY(1.2) scaleX(0.95); opacity: 0.85; }
          100% { transform: translateY(0) scaleY(1) scaleX(1); opacity: 0.6; }
        }

        .lightning-border {
          animation: lightning-pulse 2s ease-in-out infinite !important;
        }
        @keyframes lightning-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.6), 0 0 40px rgba(139, 92, 246, 0.4) !important; }
          50% { box-shadow: 0 0 30px rgba(139, 92, 246, 0.9), 0 0 60px rgba(139, 92, 246, 0.6), 0 0 80px rgba(139, 92, 246, 0.3) !important; }
        }

        .dragon-particle {
          animation: dragon-orbit 3s linear infinite !important;
        }
        @keyframes dragon-orbit {
          0% { transform: translate(-50%, -50%) rotate(0deg) translateX(80px) rotate(0deg); }
          100% { transform: translate(-50%, -50%) rotate(360deg) translateX(80px) rotate(-360deg); }
        }

        .dragon-glow {
          animation: dragon-pulse 2s ease-in-out infinite !important;
        }
        @keyframes dragon-pulse {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
      <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(15, 23, 42, 0.75)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px',
      animation: 'fadeIn 0.2s ease-out'
    }}
    onClick={onClose}
    >
      <div style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.95) 100%)',
        backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        border: '1px solid rgba(255, 255, 255, 0.5)',
        borderRadius: '24px',
        width: '100%',
        maxWidth: '520px',
        maxHeight: '85vh',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', Roboto, sans-serif",
        overflow: 'hidden',
        animation: 'modalSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }}
      onClick={(e) => e.stopPropagation()}
      >
        {/* Modern Header */}
        <div style={{
          background: 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)',
          padding: '32px 24px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative background pattern */}
          <div style={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none'
          }} />

          <div style={{
            position: 'relative',
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                width: '56px',
                height: '56px',
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
                animation: 'float 3s ease-in-out infinite'
              }}>
                🪙
              </div>
              <div>
                <h2 style={{
                  margin: 0,
                  fontSize: '24px',
                  fontWeight: '700',
                  color: 'white',
                  letterSpacing: '-0.02em',
                  marginBottom: '6px',
                  textShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                }}>
                  Token Wallet
                </h2>
                <div style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '8px'
                }}>
                  <p style={{
                    margin: 0,
                    fontSize: '32px',
                    fontWeight: '700',
                    color: 'white',
                    fontVariantNumeric: 'tabular-nums'
                  }}>
                    {tokenData.balance.toLocaleString()}
                  </p>
                  <span style={{
                    fontSize: '14px',
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: '600'
                  }}>
                    tokens
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={onClose}
              style={{
                background: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '12px',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                cursor: 'pointer',
                fontSize: '20px',
                transition: 'all 0.2s ease',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              ✕
            </button>
          </div>

          {/* Streak indicator if active */}
          {tokenData.streakDays > 0 && (
            <div style={{
              marginTop: '16px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              background: 'rgba(245, 158, 11, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              borderRadius: '12px',
              padding: '8px 12px',
              fontSize: '14px',
              fontWeight: '600',
              color: 'white'
            }}>
              <span>🔥</span>
              {tokenData.streakDays} day streak!
            </div>
          )}
        </div>

        {/* iOS-Style Segmented Tab Navigation */}
        <div style={{
          padding: '12px 16px',
          background: 'rgba(248, 250, 252, 0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(226, 232, 240, 0.6)'
        }}>
          <div
            className="token-wallet-tabs"
            style={{
            display: 'flex',
            background: 'rgba(226, 232, 240, 0.5)',
            backdropFilter: 'blur(10px)',
            borderRadius: '14px',
            padding: '3px',
            gap: '2px',
            position: 'relative',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            {[
              { id: 'wallet', label: 'Wallet', icon: '💰' },
              { id: 'daily', label: 'Daily', icon: '📋' },
              { id: 'inventory', label: 'Items', icon: '🎒' },
              { id: 'trade', label: 'Trade', icon: '🔄' },
              { id: 'marketplace', label: 'Shop', icon: '🏪' },
              { id: 'earn', label: 'Earn', icon: '🎯' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  position: 'relative',
                  flex: '1 1 0',
                  minWidth: '0',
                  padding: '7px 8px',
                  border: 'none',
                  background: activeTab === tab.id ?
                    'white' :
                    'transparent',
                  color: activeTab === tab.id ?
                    '#0ea5e9' :
                    '#64748b',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: activeTab === tab.id ? '600' : '500',
                  borderRadius: '10px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '3px',
                  whiteSpace: 'nowrap',
                  boxShadow: activeTab === tab.id ?
                    '0 2px 8px rgba(14, 165, 233, 0.15), 0 1px 2px rgba(0, 0, 0, 0.05)' :
                    'none',
                  zIndex: activeTab === tab.id ? 2 : 1
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.5)';
                    e.currentTarget.style.color = '#334155';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#64748b';
                  }
                }}
              >
                <span style={{ fontSize: '14px' }}>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div style={{
          padding: '20px',
          maxHeight: '55vh',
          overflowY: 'auto',
          fontSize: TappyLabsBranding.typography.scale.sm
        }}>
          {activeTab === 'wallet' && (
            <WalletTab tokenData={tokenData} userTier={userTier} user={user} />
          )}

          {activeTab === 'daily' && (
            <DailyTab user={user} onTokensEarned={fetchTokenData} />
          )}

          {activeTab === 'inventory' && (
            <InventoryTab tokenData={tokenData} userTier={userTier} onEquipItem={handleEquipItem} />
          )}

          {activeTab === 'trade' && (
            <TradeTab tokenBalance={tokenData.balance} user={user} userTier={userTier} />
          )}
          
          {activeTab === 'marketplace' && (
            <MarketplaceTab bounties={bounties} tokenBalance={tokenData.balance} />
          )}
          
          {activeTab === 'earn' && (
            <EarnTab userTier={userTier} />
          )}
        </div>
      </div>
    </div>
    </>
  );

  return createPortal(modalContent, document.body);
};

// Trade Tab Component - 2025 Design with Friend Collaboration Integration
const TradeTab = ({ tokenBalance, user, userTier }) => {
  const [transferHistory, setTransferHistory] = useState([]);
  const [recentRecipients, setRecentRecipients] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [sendAmount, setSendAmount] = useState('');
  const [recipientUsername, setRecipientUsername] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestAmount, setRequestAmount] = useState('');
  const [requestFromUsername, setRequestFromUsername] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  // Friend collaboration integration
  const [connectedFriends, setConnectedFriends] = useState([]);
  const [friendsLoading, setFriendsLoading] = useState(true);
  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  useEffect(() => {
    fetchTransferHistory();
    fetchConnectedFriends();
  }, []);

  // Fetch friends - same pattern as FriendsOnlineWidget
  const fetchConnectedFriends = async () => {
    try {
      setFriendsLoading(true);
      const response = await axios.get('/api/voice/friends/online');
      const friends = response.data.friends || [];

      // Map to consistent format with online status
      const mappedFriends = friends.map(f => ({
        ...f,
        username: f.username || f.friend_username,
        is_online: true,
        streak_days: f.streak_days || 0
      }));

      // Sort by streak
      mappedFriends.sort((a, b) => (b.streak_days || 0) - (a.streak_days || 0));

      setConnectedFriends(mappedFriends.slice(0, 6)); // Top 6 friends
    } catch (error) {
      console.error('Error fetching friends:', error);
    } finally {
      setFriendsLoading(false);
    }
  };

  const fetchTransferHistory = async () => {
    try {
      const response = await axios.get('/api/tokens/transfer-history');
      const transfers = response.data.transfers || [];
      setTransferHistory(transfers);

      // Extract recent unique recipients (people you sent to)
      const currentUser = user?.username || user;
      const recipients = transfers
        .filter(t => t.transfer_type === 'gift' && t.sender_username === currentUser)
        .reduce((acc, t) => {
          if (!acc.find(r => r.username === t.recipient_username)) {
            acc.push({
              username: t.recipient_username,
              lastAmount: t.amount,
              timestamp: t.timestamp
            });
          }
          return acc;
        }, [])
        .slice(0, 4); // Top 4 recent recipients

      setRecentRecipients(recipients);
    } catch (error) {
      console.error('Error fetching transfer history:', error);
    }
  };

  const handleQuickSend = (recipient, amount) => {
    setSelectedRecipient(recipient);
    setRecipientUsername(recipient.username);
    setSendAmount(amount.toString());
    setShowSendModal(true);
  };

  const handleSendTokens = async () => {
    if (!recipientUsername || !sendAmount || sendAmount <= 0) {
      setToast({ show: true, message: 'Please select a friend and enter an amount', type: 'warning' });
      return;
    }

    setSendLoading(true);
    try {
      const response = await axios.post('/api/tokens/transfer', {
        recipient_username: recipientUsername,
        amount: parseInt(sendAmount),
        transfer_type: 'gift',
        message: sendMessage || `Thanks for helping! 🎓`
      });

      if (response.data.success) {
        setToast({ show: true, message: `Sent ${sendAmount} tokens to ${recipientUsername}! 🎁`, type: 'success' });
        setShowSendModal(false);
        setSendAmount('');
        setRecipientUsername('');
        setSendMessage('');
        setSelectedRecipient(null);
        fetchTransferHistory();

        // Refresh token balance
        if (window.refreshTokenBalance) {
          window.refreshTokenBalance();
        }
        if (window.refreshTokenWallet) {
          window.refreshTokenWallet();
        }
      }
    } catch (error) {
      setToast({ show: true, message: error.response?.data?.error || 'Error sending tokens', type: 'error' });
    } finally {
      setSendLoading(false);
    }
  };

  const handleApproveRequest = async (requestId, amount, requesterName) => {
    try {
      const response = await axios.post(`/api/tokens/request/${requestId}/approve`);
      if (response.data.success) {
        setToast({ show: true, message: `Sent ${amount} tokens to ${requesterName}! ✅`, type: 'success' });
        fetchTransferHistory();
        if (window.refreshTokenBalance) window.refreshTokenBalance();
        if (window.refreshTokenWallet) window.refreshTokenWallet();
      }
    } catch (error) {
      setToast({ show: true, message: error.response?.data?.error || 'Error approving request', type: 'error' });
    }
  };

  const handleRequestTokens = async () => {
    if (!requestFromUsername || !requestAmount || requestAmount <= 0) {
      setToast({ show: true, message: 'Please select a friend and enter an amount', type: 'warning' });
      return;
    }

    setRequestLoading(true);
    try {
      const response = await axios.post('/api/tokens/request', {
        from_username: requestFromUsername,
        amount: parseInt(requestAmount),
        message: requestMessage || `Can you send me some tokens? 🙏`
      });

      if (response.data.success) {
        setToast({ show: true, message: `Request sent to ${requestFromUsername}! 📨`, type: 'success' });
        setShowRequestModal(false);
        setRequestAmount('');
        setRequestFromUsername('');
        setRequestMessage('');
        fetchTransferHistory();
      }
    } catch (error) {
      setToast({ show: true, message: error.response?.data?.error || 'Error requesting tokens', type: 'error' });
    } finally {
      setRequestLoading(false);
    }
  };

  return (
    <div>
      {/* Toast Notification */}
      <ToastNotification
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, show: false })}
        duration={3000}
      />
      <h3 style={{
        margin: '0 0 20px 0',
        fontSize: '16px',
        fontWeight: '700',
        color: '#0f172a',
        letterSpacing: '-0.01em'
      }}>
        Token Trading
      </h3>

      {/* Your Balance Card */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(99, 102, 241, 0.2)',
        borderRadius: '16px',
        padding: '16px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div>
          <div style={{
            fontSize: '12px',
            color: '#64748b',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: '4px'
          }}>
            Available to Send
          </div>
          <div style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#6366f1',
            fontVariantNumeric: 'tabular-nums'
          }}>
            {tokenBalance.toLocaleString()} 🪙
          </div>
        </div>
      </div>

      {/* Quick Send - Recent Recipients */}
      {recentRecipients.length > 0 && (
        <div style={{
          marginBottom: '20px'
        }}>
          <div style={{
            fontSize: '13px',
            fontWeight: '700',
            color: '#0f172a',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span>⚡</span> Quick Send
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '10px'
          }}>
            {recentRecipients.map((recipient, idx) => (
              <div
                key={idx}
                style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  border: '2px solid rgba(99, 102, 241, 0.15)',
                  borderRadius: '12px',
                  padding: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.4)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = 'rgba(99, 102, 241, 0.15)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{
                  fontSize: '12px',
                  fontWeight: '700',
                  color: '#0f172a',
                  marginBottom: '8px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {recipient.username}
                </div>
                <div style={{
                  display: 'flex',
                  gap: '4px'
                }}>
                  {[5, 10, 25].map(amount => (
                    <button
                      key={amount}
                      onClick={() => handleQuickSend(recipient, amount)}
                      style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, #6366f1, #059669)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        padding: '6px 8px',
                        fontSize: '11px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(99, 102, 241, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      {amount} 🪙
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Study Buddies - Friends you've collaborated with */}
      {connectedFriends.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            fontSize: '13px',
            fontWeight: '700',
            color: '#0f172a',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span>👯</span> Study Buddies
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '8px'
          }}>
            {connectedFriends.map((friend, idx) => (
              <div
                key={idx}
                onClick={() => {
                  setRecipientUsername(friend.username);
                  setSendMessage(`Thanks for studying with me! 📚`);
                  setShowSendModal(true);
                }}
                style={{
                  background: friend.is_online
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(6, 182, 212, 0.1))'
                    : 'rgba(248, 250, 252, 0.8)',
                  border: friend.is_online
                    ? '2px solid rgba(16, 185, 129, 0.3)'
                    : '1px solid rgba(203, 213, 225, 0.5)',
                  borderRadius: '10px',
                  padding: '10px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'center',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Online indicator */}
                {friend.is_online && (
                  <div style={{
                    position: 'absolute',
                    top: '6px',
                    right: '6px',
                    width: '8px',
                    height: '8px',
                    background: '#10B981',
                    borderRadius: '50%',
                    boxShadow: '0 0 6px rgba(16, 185, 129, 0.6)'
                  }} />
                )}

                {/* Avatar */}
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: friend.is_online
                    ? 'linear-gradient(135deg, #10B981, #06B6D4)'
                    : 'linear-gradient(135deg, #94A3B8, #64748B)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 6px',
                  fontSize: '16px',
                  color: 'white',
                  fontWeight: '700'
                }}>
                  {friend.username?.charAt(0)?.toUpperCase() || '?'}
                </div>

                {/* Username */}
                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#0f172a',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  marginBottom: '4px'
                }}>
                  {friend.username}
                </div>

                {/* Streak badge */}
                {friend.streak_days > 0 && (
                  <div style={{
                    fontSize: '10px',
                    color: '#F59E0B',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2px'
                  }}>
                    🔥 {friend.streak_days}
                  </div>
                )}

                {/* Current subject if online */}
                {friend.is_online && friend.current_subject && (
                  <div style={{
                    fontSize: '9px',
                    color: '#10B981',
                    marginTop: '2px'
                  }}>
                    Studying {friend.current_subject}
                  </div>
                )}
              </div>
            ))}
          </div>
          {connectedFriends.length === 0 && !friendsLoading && (
            <div style={{
              textAlign: 'center',
              padding: '16px',
              color: '#64748b',
              fontSize: '12px'
            }}>
              Study with friends to see them here!
            </div>
          )}
        </div>
      )}

      {/* Trade with Friends Card */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(6, 182, 212, 0.1))',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(14, 165, 233, 0.2)',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '24px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background decoration */}
        <div style={{
          position: 'absolute',
          top: '-20px',
          right: '-20px',
          fontSize: '80px',
          opacity: 0.08,
          transform: 'rotate(-15deg)'
        }}>
          🤝
        </div>

        <div style={{
          position: 'relative',
          zIndex: 1
        }}>
          <div style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#0f172a',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span>🤝</span> Trade with Friends
          </div>
          <div style={{
            fontSize: '13px',
            color: '#64748b',
            marginBottom: '16px',
            lineHeight: 1.5
          }}>
            Send tokens to friends for helping you study, or trade for rewards
          </div>

          {/* Trading Actions */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <button
              onClick={() => setShowSendModal(true)}
              style={{
                background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)',
                border: 'none',
                borderRadius: '12px',
                color: 'white',
                padding: '12px 16px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                transition: 'all 0.2s ease',
                boxShadow: '0 2px 8px rgba(14, 165, 233, 0.2)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(14, 165, 233, 0.2)';
              }}
            >
              <span>💸</span> Send Tokens
            </button>
            <button
              onClick={() => setShowRequestModal(true)}
              style={{
              background: 'rgba(14, 165, 233, 0.1)',
              border: '1px solid rgba(14, 165, 233, 0.3)',
              borderRadius: '12px',
              color: '#0ea5e9',
              padding: '12px 16px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(14, 165, 233, 0.15)';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(14, 165, 233, 0.1)';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
            >
              <span>🎁</span> Request
            </button>
          </div>
        </div>
      </div>

      {/* Modern Send Tokens Modal */}
      {showSendModal && (
        <>
          {/* Backdrop */}
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(8px)',
            zIndex: 10000,
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={() => setShowSendModal(false)}
          />

          {/* Modal */}
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
            backdropFilter: 'blur(40px)',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            borderRadius: '20px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
            zIndex: 10001,
            minWidth: '340px',
            maxWidth: '400px',
            animation: 'modalSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)',
              padding: '24px',
              borderRadius: '20px 20px 0 0',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at 30% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)'
              }} />
              <div style={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}>
                  💸
                </div>
                <h4 style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '700',
                  color: 'white',
                  letterSpacing: '-0.01em'
                }}>
                  Send Tokens
                </h4>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#334155',
                  marginBottom: '8px'
                }}>
                  Send to Friend
                </label>
                {connectedFriends.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* Friend selection grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '8px',
                      maxHeight: '180px',
                      overflowY: 'auto',
                      padding: '4px'
                    }}>
                      {connectedFriends.map((friend, idx) => (
                        <div
                          key={idx}
                          onClick={() => setRecipientUsername(friend.username)}
                          style={{
                            padding: '10px 8px',
                            borderRadius: '10px',
                            border: recipientUsername === friend.username
                              ? '2px solid #0ea5e9'
                              : '1.5px solid rgba(203, 213, 225, 0.6)',
                            background: recipientUsername === friend.username
                              ? 'linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(6, 182, 212, 0.1))'
                              : 'white',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            textAlign: 'center'
                          }}
                        >
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: friend.is_online
                              ? 'linear-gradient(135deg, #10B981, #06B6D4)'
                              : 'linear-gradient(135deg, #94A3B8, #64748B)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 6px',
                            fontSize: '14px',
                            color: 'white',
                            fontWeight: '700',
                            position: 'relative'
                          }}>
                            {friend.username?.charAt(0)?.toUpperCase() || '?'}
                            {friend.is_online && (
                              <div style={{
                                position: 'absolute',
                                bottom: '0',
                                right: '0',
                                width: '10px',
                                height: '10px',
                                background: '#10B981',
                                borderRadius: '50%',
                                border: '2px solid white'
                              }} />
                            )}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#0f172a',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {friend.username}
                          </div>
                          {friend.streak_days > 0 && (
                            <div style={{ fontSize: '9px', color: '#F59E0B' }}>
                              🔥 {friend.streak_days}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {recipientUsername && (
                      <div style={{
                        fontSize: '12px',
                        color: '#0ea5e9',
                        fontWeight: '600',
                        textAlign: 'center'
                      }}>
                        Sending to: {recipientUsername}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    background: 'rgba(248, 250, 252, 0.8)',
                    borderRadius: '12px',
                    border: '1px dashed rgba(203, 213, 225, 0.8)'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>👯</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      Study with friends to add them here!
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#334155',
                  marginBottom: '8px'
                }}>
                  Amount
                  <span style={{
                    fontSize: '12px',
                    fontWeight: '500',
                    color: '#64748b',
                    marginLeft: '8px'
                  }}>
                    (Max: {tokenBalance})
                  </span>
                </label>
                <input
                  type="number"
                  value={sendAmount}
                  onChange={(e) => setSendAmount(e.target.value)}
                  max={tokenBalance}
                  placeholder="0"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    fontSize: '14px',
                    border: '1.5px solid rgba(203, 213, 225, 0.8)',
                    borderRadius: '12px',
                    background: 'white',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    fontFamily: 'inherit',
                    MozAppearance: 'textfield',
                    WebkitAppearance: 'none',
                    appearance: 'none'
                  }}
                  onWheel={(e) => e.target.blur()}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0ea5e9';
                    e.target.style.boxShadow = '0 0 0 3px rgba(14, 165, 233, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(203, 213, 225, 0.8)';
                    e.target.style.boxShadow = 'none';
                  }}
                />

                {/* Quick Amount Buttons */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '8px',
                  marginTop: '12px'
                }}>
                  {[5, 10, 25, 50].map(amount => (
                    <button
                      key={amount}
                      onClick={() => setSendAmount(amount.toString())}
                      disabled={amount > tokenBalance}
                      style={{
                        background: sendAmount === amount.toString()
                          ? 'linear-gradient(135deg, #0ea5e9, #06b6d4)'
                          : amount > tokenBalance
                            ? 'rgba(203, 213, 225, 0.3)'
                            : 'rgba(14, 165, 233, 0.1)',
                        border: sendAmount === amount.toString()
                          ? 'none'
                          : '1.5px solid rgba(14, 165, 233, 0.3)',
                        borderRadius: '8px',
                        color: sendAmount === amount.toString()
                          ? 'white'
                          : amount > tokenBalance
                            ? '#94a3b8'
                            : '#0ea5e9',
                        padding: '8px 12px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: amount > tokenBalance ? 'not-allowed' : 'pointer',
                        transition: 'all 0.15s ease',
                        opacity: amount > tokenBalance ? 0.5 : 1
                      }}
                      onMouseEnter={(e) => {
                        if (amount <= tokenBalance && sendAmount !== amount.toString()) {
                          e.currentTarget.style.background = 'rgba(14, 165, 233, 0.15)';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (amount <= tokenBalance && sendAmount !== amount.toString()) {
                          e.currentTarget.style.background = 'rgba(14, 165, 233, 0.1)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                    >
                      {amount} 🪙
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#334155',
                  marginBottom: '8px'
                }}>
                  Message (optional)
                </label>
                <input
                  type="text"
                  value={sendMessage}
                  onChange={(e) => setSendMessage(e.target.value)}
                  placeholder="Thanks for helping me study!"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    fontSize: '14px',
                    border: '1.5px solid rgba(203, 213, 225, 0.8)',
                    borderRadius: '12px',
                    background: 'white',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#0ea5e9';
                    e.target.style.boxShadow = '0 0 0 3px rgba(14, 165, 233, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(203, 213, 225, 0.8)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleSendTokens}
                  disabled={sendLoading}
                  style={{
                    flex: 1,
                    background: sendLoading ? '#94a3b8' : 'linear-gradient(135deg, #0ea5e9, #06b6d4)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: sendLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: sendLoading ? 'none' : '0 2px 8px rgba(14, 165, 233, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    if (!sendLoading) {
                      e.currentTarget.style.transform = 'translateY(-1px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(14, 165, 233, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!sendLoading) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(14, 165, 233, 0.3)';
                    }
                  }}
                >
                  {sendLoading ? 'Sending...' : 'Send Tokens'}
                </button>
                <button
                  onClick={() => setShowSendModal(false)}
                  style={{
                    padding: '12px 20px',
                    background: 'rgba(203, 213, 225, 0.3)',
                    border: '1px solid rgba(203, 213, 225, 0.6)',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#64748b',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(203, 213, 225, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(203, 213, 225, 0.3)';
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Request Tokens Modal */}
      {showRequestModal && (
        <>
          {/* Backdrop */}
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(8px)',
            zIndex: 10000,
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={() => setShowRequestModal(false)}
          />

          {/* Modal */}
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.98) 100%)',
            backdropFilter: 'blur(40px)',
            border: '1px solid rgba(255, 255, 255, 0.8)',
            borderRadius: '20px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
            zIndex: 10001,
            minWidth: '340px',
            maxWidth: '400px',
            animation: 'modalSlideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            {/* Modal Header */}
            <div style={{
              background: 'linear-gradient(135deg, #10b981, #059669)',
              padding: '24px',
              borderRadius: '20px 20px 0 0',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at 30% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)'
              }} />
              <div style={{
                position: 'relative',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px'
                }}>
                  🎁
                </div>
                <h4 style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '700',
                  color: 'white',
                  letterSpacing: '-0.01em'
                }}>
                  Request Tokens
                </h4>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#334155',
                  marginBottom: '8px'
                }}>
                  Request From Friend
                </label>
                {connectedFriends.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {/* Friend selection grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '8px',
                      maxHeight: '180px',
                      overflowY: 'auto',
                      padding: '4px'
                    }}>
                      {connectedFriends.map((friend, idx) => (
                        <div
                          key={idx}
                          onClick={() => setRequestFromUsername(friend.username)}
                          style={{
                            padding: '10px 8px',
                            borderRadius: '10px',
                            border: requestFromUsername === friend.username
                              ? '2px solid #10b981'
                              : '1.5px solid rgba(203, 213, 225, 0.6)',
                            background: requestFromUsername === friend.username
                              ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(6, 182, 212, 0.1))'
                              : 'white',
                            cursor: 'pointer',
                            transition: 'all 0.15s ease',
                            textAlign: 'center'
                          }}
                        >
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '50%',
                            background: friend.is_online
                              ? 'linear-gradient(135deg, #10B981, #06B6D4)'
                              : 'linear-gradient(135deg, #94A3B8, #64748B)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 6px',
                            fontSize: '14px',
                            color: 'white',
                            fontWeight: '700',
                            position: 'relative'
                          }}>
                            {friend.username?.charAt(0)?.toUpperCase() || '?'}
                            {friend.is_online && (
                              <div style={{
                                position: 'absolute',
                                bottom: '0',
                                right: '0',
                                width: '10px',
                                height: '10px',
                                background: '#10B981',
                                borderRadius: '50%',
                                border: '2px solid white'
                              }} />
                            )}
                          </div>
                          <div style={{
                            fontSize: '11px',
                            fontWeight: '600',
                            color: '#0f172a',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {friend.username}
                          </div>
                          {friend.streak_days > 0 && (
                            <div style={{ fontSize: '9px', color: '#F59E0B' }}>
                              🔥 {friend.streak_days}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {requestFromUsername && (
                      <div style={{
                        fontSize: '12px',
                        color: '#10b981',
                        fontWeight: '600',
                        textAlign: 'center'
                      }}>
                        Requesting from: {requestFromUsername}
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    background: 'rgba(248, 250, 252, 0.8)',
                    borderRadius: '12px',
                    border: '1px dashed rgba(203, 213, 225, 0.8)'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>👯</div>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      Study with friends to add them here!
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#334155',
                  marginBottom: '8px'
                }}>
                  Amount
                </label>
                <input
                  type="number"
                  value={requestAmount}
                  onChange={(e) => setRequestAmount(e.target.value)}
                  placeholder="0"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    fontSize: '14px',
                    border: '1.5px solid rgba(203, 213, 225, 0.8)',
                    borderRadius: '12px',
                    background: 'white',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    fontFamily: 'inherit',
                    MozAppearance: 'textfield',
                    WebkitAppearance: 'none',
                    appearance: 'none'
                  }}
                  onWheel={(e) => e.target.blur()}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(203, 213, 225, 0.8)';
                    e.target.style.boxShadow = 'none';
                  }}
                />

                {/* Quick Amount Buttons */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '8px',
                  marginTop: '12px'
                }}>
                  {[5, 10, 25, 50].map(amount => (
                    <button
                      key={amount}
                      onClick={() => setRequestAmount(amount.toString())}
                      style={{
                        background: requestAmount === amount.toString()
                          ? 'linear-gradient(135deg, #10b981, #059669)'
                          : 'rgba(16, 185, 129, 0.1)',
                        border: requestAmount === amount.toString()
                          ? 'none'
                          : '1.5px solid rgba(16, 185, 129, 0.3)',
                        borderRadius: '8px',
                        color: requestAmount === amount.toString()
                          ? 'white'
                          : '#10b981',
                        padding: '8px 12px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease'
                      }}
                      onMouseEnter={(e) => {
                        if (requestAmount !== amount.toString()) {
                          e.currentTarget.style.background = 'rgba(16, 185, 129, 0.15)';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (requestAmount !== amount.toString()) {
                          e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }
                      }}
                    >
                      {amount} 🪙
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#334155',
                  marginBottom: '8px'
                }}>
                  Message (optional)
                </label>
                <input
                  type="text"
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  placeholder="Can you help me out? 🙏"
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    fontSize: '14px',
                    border: '1.5px solid rgba(203, 213, 225, 0.8)',
                    borderRadius: '12px',
                    background: 'white',
                    transition: 'all 0.2s ease',
                    outline: 'none',
                    fontFamily: 'inherit'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#10b981';
                    e.target.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(203, 213, 225, 0.8)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <button
                  onClick={handleRequestTokens}
                  disabled={requestLoading}
                  style={{
                    background: requestLoading
                      ? 'rgba(16, 185, 129, 0.5)'
                      : 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    padding: '14px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: requestLoading ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 8px rgba(16, 185, 129, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    if (!requestLoading) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!requestLoading) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.2)';
                    }
                  }}
                >
                  {requestLoading ? 'Requesting...' : 'Send Request'}
                </button>
                <button
                  onClick={() => setShowRequestModal(false)}
                  style={{
                    background: 'rgba(203, 213, 225, 0.3)',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '14px 20px',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#64748b',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(203, 213, 225, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(203, 213, 225, 0.3)';
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Trade History */}
      <div>
        <h4 style={{
          margin: '0 0 16px 0',
          fontSize: '15px',
          fontWeight: '700',
          color: '#0f172a',
          letterSpacing: '-0.01em'
        }}>
          Recent Trades
        </h4>

        {transferHistory.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {transferHistory.slice(0, 10).map((transfer, index) => {
              const isSent = transfer.type === 'sent';
              const isPendingRequest = transfer.transfer_type === 'request' && transfer.status === 'pending' && isSent;
              return (
                <div key={index} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '14px 16px',
                  background: isPendingRequest ?
                    'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(234, 88, 12, 0.1))' :
                    isSent ?
                      'linear-gradient(135deg, rgba(239, 68, 68, 0.05), rgba(220, 38, 38, 0.05))' :
                      'linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(5, 150, 105, 0.05))',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  border: isPendingRequest ?
                    '2px solid rgba(245, 158, 11, 0.4)' :
                    `1px solid ${isSent ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.2)'}`,
                  transition: 'all 0.2s ease'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '10px',
                      background: isPendingRequest ?
                        'linear-gradient(135deg, #f59e0b, #ea580c)' :
                        isSent ?
                          'linear-gradient(135deg, #ef4444, #dc2626)' :
                          'linear-gradient(135deg, #10b981, #059669)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '16px',
                      color: 'white',
                      fontWeight: '700',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                    }}>
                      {isPendingRequest ? '🙏' : isSent ? '→' : '←'}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#0f172a',
                        marginBottom: '2px'
                      }}>
                        {isPendingRequest ? `${transfer.other_user} requested` : transfer.other_user}
                      </div>
                      {transfer.message && (
                        <div style={{
                          fontSize: '12px',
                          color: '#64748b',
                          marginBottom: '2px',
                          fontStyle: 'italic'
                        }}>
                          "{transfer.message}"
                        </div>
                      )}
                      <div style={{
                        fontSize: '11px',
                        color: '#94a3b8'
                      }}>
                        {new Date(transfer.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  {isPendingRequest ? (
                    <button
                      onClick={() => handleApproveRequest(transfer.id, Math.abs(transfer.amount), transfer.other_user)}
                      style={{
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        border: 'none',
                        borderRadius: '8px',
                        color: 'white',
                        padding: '8px 16px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      Send {Math.abs(transfer.amount)} 🪙
                    </button>
                  ) : (
                    <div style={{
                      fontSize: '16px',
                      fontWeight: '700',
                      color: isSent ? '#ef4444' : '#10b981',
                      fontVariantNumeric: 'tabular-nums'
                    }}>
                      {isSent ? '-' : '+'}{Math.abs(transfer.amount)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '64px 20px',
            background: 'rgba(248, 250, 252, 0.5)',
            borderRadius: '16px',
            border: '1px dashed rgba(203, 213, 225, 0.8)'
          }}>
            <div style={{
              fontSize: '72px',
              marginBottom: '16px',
              filter: 'grayscale(0.3) opacity(0.6)'
            }}>
              🔄
            </div>
            <div style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#334155',
              marginBottom: '6px'
            }}>
              No Trades Yet
            </div>
            <div style={{
              fontSize: '13px',
              color: '#64748b'
            }}>
              Start trading with friends to see your history!
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Wallet Tab Component - 2025 Design
const WalletTab = ({ tokenData, userTier, user }) => {
  // Get profile theme background
  const getProfileThemeBackground = (theme) => {
    switch (theme) {
      case 'galaxy':
        return 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e1b4b 100%), url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'100\' height=\'100\'%3E%3Ccircle cx=\'10\' cy=\'10\' r=\'1\' fill=\'%23ffffff\' opacity=\'0.3\'/%3E%3Ccircle cx=\'40\' cy=\'30\' r=\'1.5\' fill=\'%23ffffff\' opacity=\'0.5\'/%3E%3Ccircle cx=\'70\' cy=\'20\' r=\'1\' fill=\'%23ffffff\' opacity=\'0.4\'/%3E%3Ccircle cx=\'90\' cy=\'60\' r=\'1\' fill=\'%23ffffff\' opacity=\'0.3\'/%3E%3Ccircle cx=\'30\' cy=\'80\' r=\'1.5\' fill=\'%23ffffff\' opacity=\'0.6\'/%3E%3C/svg%3E")';
      default:
        return 'linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(16, 185, 129, 0.1))';
    }
  };

  // Get username color
  const getUsernameColor = (color) => {
    switch (color) {
      case 'gold':
        return '#F59E0B';
      default:
        return '#0f172a';
    }
  };

  // Get border style based on profile_border
  const getBorderStyle = (borderType) => {
    switch (borderType) {
      case 'basic':
        return {
          border: '3px solid #9CA3AF',
          boxShadow: '0 0 20px rgba(156, 163, 175, 0.4)'
        };
      case 'lightning':
        return {
          border: '3px solid #8B5CF6',
          boxShadow: '0 0 20px rgba(139, 92, 246, 0.6), 0 0 40px rgba(139, 92, 246, 0.4)',
          animation: 'lightning-pulse 2s ease-in-out infinite'
        };
      default:
        return {
          border: '2px solid rgba(245, 158, 11, 0.3)'
        };
    }
  };

  return (
    <div>
      {/* Profile Preview Card - Shows only EQUIPPED items */}
      {(tokenData.equippedProfileTheme || tokenData.usernameColor || tokenData.equippedProfileBorder || tokenData.equippedProfileEffect) && (
        <div style={{
          background: getProfileThemeBackground(tokenData.equippedProfileTheme),
          backgroundSize: 'cover',
          backdropFilter: 'blur(10px)',
          ...getBorderStyle(tokenData.equippedProfileBorder),
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '20px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: tokenData.equippedProfileBorder ? getBorderStyle(tokenData.equippedProfileBorder).boxShadow : '0 8px 24px rgba(0, 0, 0, 0.12)'
        }}>
          <div style={{
            position: 'relative',
            zIndex: 1,
            textAlign: 'center'
          }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '32px',
              margin: '0 auto 12px',
              boxShadow: '0 4px 16px rgba(99, 102, 241, 0.4)',
              border: '3px solid rgba(255, 255, 255, 0.2)'
            }}>
              👤
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: '700',
              color: getUsernameColor(tokenData.usernameColor),
              marginBottom: '4px',
              textShadow: tokenData.usernameColor === 'gold' ? '0 2px 8px rgba(245, 158, 11, 0.5)' : 'none',
              filter: tokenData.usernameColor === 'gold' ? 'drop-shadow(0 0 8px rgba(245, 158, 11, 0.6))' : 'none'
            }}>
              {user?.username || user || 'Your Username'}
            </div>
            <div style={{
              fontSize: '12px',
              color: 'rgba(255, 255, 255, 0.8)',
              marginTop: '12px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              justifyContent: 'center'
            }}>
              {tokenData.equippedProfileTheme && (
                <span style={{
                  background: 'rgba(99, 102, 241, 0.3)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(99, 102, 241, 0.4)',
                  padding: '4px 10px',
                  borderRadius: '8px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  🌌 {tokenData.equippedProfileTheme} theme
                </span>
              )}
              {tokenData.usernameColor && (
                <span style={{
                  background: 'rgba(245, 158, 11, 0.3)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(245, 158, 11, 0.4)',
                  padding: '4px 10px',
                  borderRadius: '8px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  ✨ {tokenData.usernameColor} username
                </span>
              )}
              {tokenData.equippedProfileBorder && (
                <span style={{
                  background: tokenData.equippedProfileBorder === 'lightning' ? 'rgba(139, 92, 246, 0.3)' : 'rgba(156, 163, 175, 0.3)',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${tokenData.equippedProfileBorder === 'lightning' ? 'rgba(139, 92, 246, 0.4)' : 'rgba(156, 163, 175, 0.4)'}`,
                  padding: '4px 10px',
                  borderRadius: '8px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  {tokenData.equippedProfileBorder === 'lightning' ? '⚡' : '🖼️'} {tokenData.equippedProfileBorder} border
                </span>
              )}
              {tokenData.equippedProfileEffect && (
                <span style={{
                  background: tokenData.equippedProfileEffect === 'dragon' ? 'rgba(245, 158, 11, 0.3)' : 'rgba(239, 68, 68, 0.3)',
                  backdropFilter: 'blur(10px)',
                  border: `1px solid ${tokenData.equippedProfileEffect === 'dragon' ? 'rgba(245, 158, 11, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
                  padding: '4px 10px',
                  borderRadius: '8px',
                  fontSize: '11px',
                  fontWeight: '600'
                }}>
                  {tokenData.equippedProfileEffect === 'dragon' ? '🐉' : '🔥'} {tokenData.equippedProfileEffect} effect
                </span>
              )}
            </div>
          </div>

          {/* Fire Effect Overlay */}
          {tokenData.equippedProfileEffect === 'fire' && (
            <>
              {[...Array(8)].map((_, i) => (
                <div
                  key={`flame-${i}`}
                  className="fire-flame"
                  style={{
                    position: 'absolute',
                    bottom: `${5 + (i % 3) * 5}%`,
                    left: `${(i / 8) * 100 + 2}%`,
                    width: '16px',
                    height: '35px',
                    background: 'linear-gradient(180deg, #EF4444 0%, #F59E0B 50%, #FCD34D 70%, transparent 100%)',
                    borderRadius: '50% 50% 50% 50% / 60% 60% 40% 40%',
                    opacity: 0.8,
                    filter: 'blur(1px)',
                    zIndex: 0,
                    boxShadow: '0 0 10px rgba(239, 68, 68, 0.5)'
                  }}
                />
              ))}
            </>
          )}

          {/* Dragon Aura Effect Overlay */}
          {tokenData.equippedProfileEffect === 'dragon' && (
            <>
              {/* Swirling energy particles */}
              {[...Array(12)].map((_, i) => (
                <div
                  key={`dragon-${i}`}
                  style={{
                    position: 'absolute',
                    top: `${(i / 12) * 100}%`,
                    left: '50%',
                    width: '8px',
                    height: '8px',
                    background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                    borderRadius: '50%',
                    animation: `dragon-orbit ${3 + i * 0.2}s linear infinite`,
                    opacity: 0.8,
                    boxShadow: '0 0 10px rgba(245, 158, 11, 0.8)',
                    zIndex: 0
                  }}
                />
              ))}
              {/* Mystical glow */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at center, rgba(245, 158, 11, 0.1), transparent 70%)',
                animation: 'dragon-pulse 2s ease-in-out infinite',
                zIndex: 0
              }} />
            </>
          )}

          {/* The Explorer Effect Overlay */}
          {tokenData.equippedProfileEffect === 'explorer' && (
            <>
              {/* Twinkling stars */}
              {[...Array(15)].map((_, i) => (
                <div
                  key={`star-${i}`}
                  style={{
                    position: 'absolute',
                    top: `${Math.random() * 100}%`,
                    left: `${Math.random() * 100}%`,
                    width: '4px',
                    height: '4px',
                    background: '#06b6d4',
                    borderRadius: '50%',
                    animation: `float ${2 + Math.random() * 2}s ease-in-out infinite`,
                    animationDelay: `${i * 0.2}s`,
                    opacity: 0.7,
                    boxShadow: '0 0 8px rgba(6, 182, 212, 0.8)',
                    zIndex: 0
                  }}
                />
              ))}
              {/* Cyan exploration glow */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at center, rgba(6, 182, 212, 0.15), transparent 70%)',
                animation: 'dragon-pulse 3s ease-in-out infinite',
                zIndex: 0
              }} />
              {/* Telescope icon overlay */}
              <div style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                fontSize: '20px',
                opacity: 0.7,
                animation: 'float 3s ease-in-out infinite',
                zIndex: 1
              }}>
                🔭
              </div>
            </>
          )}

          {/* Sparkle effect for premium items */}
          {(tokenData.profileTheme || tokenData.usernameColor || tokenData.profileBorder || tokenData.profileEffect) && (
            <div style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              fontSize: '24px',
              animation: 'float 2s ease-in-out infinite',
              opacity: 0.6,
              zIndex: 2
            }}>
              ✨
            </div>
          )}
        </div>
      )}

      {/* Quick Stats Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: tokenData.streakDays > 0 ? '2fr 1fr' : '1fr',
        gap: '12px',
        marginBottom: '24px'
      }}>
      {/* Total Balance Card */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(16, 185, 129, 0.1))',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(14, 165, 233, 0.2)',
        borderRadius: '16px',
        padding: '20px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          position: 'absolute',
          top: '-20px',
          right: '-20px',
          fontSize: '80px',
          opacity: 0.1
        }}>💰</div>
        <div style={{
          fontSize: '13px',
          color: '#64748b',
          fontWeight: '600',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '0.5px'
        }}>
          Total Balance
        </div>
        <div style={{
          fontSize: '36px',
          fontWeight: '700',
          color: '#0ea5e9',
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.02em'
        }}>
          {tokenData.balance.toLocaleString()}
        </div>
        <div style={{
          fontSize: '13px',
          color: '#10b981',
          fontWeight: '600',
          marginTop: '4px'
        }}>
          🪙 tokens
        </div>
      </div>

      {/* Streak Card */}
      {tokenData.streakDays > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(249, 115, 22, 0.1))',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: '16px',
          padding: '20px',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            fontSize: '40px',
            marginBottom: '8px',
            filter: 'drop-shadow(0 2px 8px rgba(245, 158, 11, 0.3))'
          }}>
            🔥
          </div>
          <div style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#f59e0b',
            fontVariantNumeric: 'tabular-nums'
          }}>
            {tokenData.streakDays}
          </div>
          <div style={{
            fontSize: '12px',
            color: '#92400e',
            fontWeight: '600',
            marginTop: '4px'
          }}>
            DAY STREAK
          </div>
        </div>
      )}
    </div>

    {/* Family Bonus Badge */}
    {userTier === 'home' && (
      <div style={{
        background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(99, 102, 241, 0.1))',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(139, 92, 246, 0.2)',
        borderRadius: '12px',
        padding: '12px 16px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
      }}>
        <span style={{ fontSize: '24px' }}>👨‍👩‍👧‍👦</span>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#6366f1',
            marginBottom: '2px'
          }}>
            Family Plan Active
          </div>
          <div style={{
            fontSize: '12px',
            color: '#64748b'
          }}>
            Sibling collaboration bonuses enabled
          </div>
        </div>
      </div>
    )}

    {/* Recent Activity Section */}
    <div style={{
      marginTop: '24px'
    }}>
      <h3 style={{
        margin: '0 0 16px 0',
        fontSize: '16px',
        fontWeight: '700',
        color: '#0f172a',
        letterSpacing: '-0.01em'
      }}>
        Recent Activity
      </h3>

      {tokenData.history.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {tokenData.history.slice(0, 5).map((transaction, index) => {
            const isFlowBonus = transaction.transaction_metadata?.flow_bonus;
            const multiplier = transaction.transaction_metadata?.multipliers?.[0]?.multiplier;

            return (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '14px 16px',
                background: isFlowBonus ?
                  'linear-gradient(135deg, rgba(245, 158, 11, 0.05), rgba(249, 115, 22, 0.05))' :
                  'rgba(255, 255, 255, 0.7)',
                backdropFilter: 'blur(10px)',
                borderRadius: '12px',
                border: isFlowBonus ?
                  '1px solid rgba(245, 158, 11, 0.2)' :
                  '1px solid rgba(226, 232, 240, 0.8)',
                transition: 'all 0.2s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = isFlowBonus ?
                  '0 4px 12px rgba(245, 158, 11, 0.15)' :
                  '0 4px 12px rgba(0, 0, 0, 0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background: isFlowBonus ?
                      'linear-gradient(135deg, #f59e0b, #f97316)' :
                      'linear-gradient(135deg, #10b981, #059669)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}>
                    {isFlowBonus ? '🔥' : '🪙'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#0f172a',
                      marginBottom: '2px'
                    }}>
                      {transaction.description || 'Token earned'}
                      {multiplier && multiplier > 1.0 && (
                        <span style={{
                          marginLeft: '8px',
                          fontSize: '11px',
                          fontWeight: '700',
                          color: 'white',
                          background: 'linear-gradient(135deg, #f59e0b, #f97316)',
                          padding: '2px 6px',
                          borderRadius: '6px'
                        }}>
                          {multiplier.toFixed(1)}×
                        </span>
                      )}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#64748b'
                    }}>
                      {new Date(transaction.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: isFlowBonus ? '#f59e0b' : '#10b981',
                  fontVariantNumeric: 'tabular-nums'
                }}>
                  +{transaction.amount}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '48px 20px',
          background: 'rgba(248, 250, 252, 0.5)',
          borderRadius: '16px',
          border: '1px dashed rgba(203, 213, 225, 0.8)'
        }}>
          <div style={{
            fontSize: '56px',
            marginBottom: '12px',
            filter: 'grayscale(0.3) opacity(0.8)'
          }}>
            🌟
          </div>
          <div style={{
            fontSize: '14px',
            color: '#64748b',
            lineHeight: 1.5
          }}>
            Start earning tokens by asking questions<br/>and completing achievements!
          </div>
        </div>
      )}
    </div>
  </div>
  );
};

// Shop Tab Component - Full catalog with categories
const MarketplaceTab = ({ bounties, tokenBalance }) => {
  const [purchaseSuccess, setPurchaseSuccess] = useState(null);
  const [purchasing, setPurchasing] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCategory, setActiveCategory] = useState('all');

  // Fetch shop catalog on mount
  useEffect(() => {
    const fetchCatalog = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/tokens/shop');
        setCatalog(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching shop catalog:', err);
        setError('Failed to load shop. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchCatalog();
  }, []);

  // Category icons and colors
  const categoryMeta = {
    mira_colors: { icon: '🎨', color: '#EC4899', name: 'Mira Colors' },
    mira_effects: { icon: '✨', color: '#8B5CF6', name: 'Glow Effects' },
    mira_eyes: { icon: '👁️', color: '#06B6D4', name: 'Eye Styles' },
    streak_protection: { icon: '🛡️', color: '#10B981', name: 'Streak Protection' },
    profile_effects: { icon: '🎭', color: '#F59E0B', name: 'Profile' }
  };

  // Item icons based on type and value
  const getItemIcon = (item) => {
    if (item.type === 'mira_orb_color') {
      const colorIcons = {
        coral: '🔴', ocean: '🔵', sunset: '🟠', midnight: '🟣', forest: '🟢', lavender: '💜'
      };
      return colorIcons[item.value] || '🎨';
    }
    if (item.type === 'mira_glow_effect') {
      const glowIcons = {
        pulse: '💫', sparkle: '✨', aurora: '🌈', rainbow: '🌟'
      };
      return glowIcons[item.value] || '✨';
    }
    if (item.type === 'mira_eye_style') {
      const eyeIcons = {
        starry: '⭐', happy: '😊', sleepy: '😴', confident: '😎'
      };
      return eyeIcons[item.value] || '👁️';
    }
    if (item.type === 'streak_freeze') return '🧊';
    if (item.type === 'weekend_amulet') return '🛡️';
    if (item.type === 'profile_border') return item.value === 'lightning' ? '⚡' : '🖼️';
    if (item.type === 'profile_effect') {
      const effectIcons = {
        fire: '🔥', dragon: '🐉', ocean_wave: '🌊', sparkle: '✨', nature: '🌿'
      };
      return effectIcons[item.value] || '🎭';
    }
    if (item.type === 'profile_theme') return '🌌';
    if (item.type === 'username_color') return '✨';
    return '🎁';
  };

  // Item gradient based on type/value
  const getItemGradient = (item) => {
    if (item.preview) {
      return `linear-gradient(135deg, ${item.preview}, ${item.preview}dd)`;
    }
    const gradients = {
      mira_orb_color: {
        coral: 'linear-gradient(135deg, #FF6B6B, #FF8E8E)',
        ocean: 'linear-gradient(135deg, #4ECDC4, #6EE7DF)',
        sunset: 'linear-gradient(135deg, #FF9F43, #FFB76B)',
        midnight: 'linear-gradient(135deg, #5F27CD, #7C4DFF)',
        forest: 'linear-gradient(135deg, #26DE81, #4AE590)',
        lavender: 'linear-gradient(135deg, #A55EEA, #C084FC)'
      },
      mira_glow_effect: {
        pulse: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
        sparkle: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
        aurora: 'linear-gradient(135deg, #06B6D4, #22D3EE)',
        rainbow: 'linear-gradient(135deg, #EC4899, #8B5CF6, #06B6D4)'
      },
      mira_eye_style: {
        starry: 'linear-gradient(135deg, #FBBF24, #F59E0B)',
        happy: 'linear-gradient(135deg, #10B981, #34D399)',
        sleepy: 'linear-gradient(135deg, #6366F1, #818CF8)',
        confident: 'linear-gradient(135deg, #EF4444, #F87171)'
      }
    };
    if (gradients[item.type]?.[item.value]) {
      return gradients[item.type][item.value];
    }
    // Fallback gradients by type
    const typeGradients = {
      streak_freeze: 'linear-gradient(135deg, #06B6D4, #22D3EE)',
      weekend_amulet: 'linear-gradient(135deg, #10B981, #34D399)',
      profile_border: 'linear-gradient(135deg, #8B5CF6, #A78BFA)',
      profile_effect: 'linear-gradient(135deg, #F59E0B, #FBBF24)',
      profile_theme: 'linear-gradient(135deg, #3B82F6, #60A5FA)',
      username_color: 'linear-gradient(135deg, #F59E0B, #FBBF24)'
    };
    return typeGradients[item.type] || 'linear-gradient(135deg, #64748B, #94A3B8)';
  };

  // Get rarity based on price
  const getRarity = (item) => {
    if (item.rare) return 'legendary';
    if (item.best_value) return 'epic';
    if (item.price >= 250) return 'epic';
    if (item.price >= 150) return 'rare';
    if (item.price >= 75) return 'uncommon';
    return 'common';
  };

  // Minecraft-style rarity system
  const rarities = {
    common: { color: '#9CA3AF', name: 'COMMON', glow: 'rgba(156, 163, 175, 0.3)' },
    uncommon: { color: '#10B981', name: 'UNCOMMON', glow: 'rgba(16, 185, 129, 0.4)' },
    rare: { color: '#3B82F6', name: 'RARE', glow: 'rgba(59, 130, 246, 0.5)' },
    epic: { color: '#A855F7', name: 'EPIC', glow: 'rgba(168, 85, 247, 0.6)' },
    legendary: { color: '#F59E0B', name: 'LEGENDARY', glow: 'rgba(245, 158, 11, 0.8)' }
  };

  // Get all items flat or filtered by category
  const getDisplayItems = () => {
    if (!catalog?.categories) return [];
    if (activeCategory === 'all') {
      return catalog.categories.flatMap(cat =>
        cat.items.map(item => ({ ...item, categoryId: cat.id, categoryName: cat.name }))
      );
    }
    const category = catalog.categories.find(c => c.id === activeCategory);
    return category ? category.items.map(item => ({ ...item, categoryId: category.id, categoryName: category.name })) : [];
  };

  const handlePurchase = async (item) => {
    if (tokenBalance >= item.price && !purchasing) {
      setPurchasing(true);

      try {
        const response = await axios.post('/api/tokens/purchase', {
          item_id: item.id,
          item_type: item.type,  // API returns 'type' not 'itemType'
          value: item.value
        });

        if (response.data.success) {
          setPurchaseSuccess({
            item: item.name,
            message: response.data.message,
            newBalance: response.data.new_balance
          });

          // Refresh token balance and wallet
          if (window.refreshTokenWallet) {
            window.refreshTokenWallet();
          }
          if (window.refreshTokenBalance) {
            window.refreshTokenBalance();
          }
        }
      } catch (error) {
        console.error('Purchase error:', error);
        const errorMsg = error.response?.data?.error || 'Purchase failed. Please try again.';
        alert(errorMsg);
      } finally {
        setPurchasing(false);
      }
    }
  };

  return (
    <div>
      {/* Success Modal */}
      {purchaseSuccess && (
        <>
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(8px)',
            zIndex: 10000
          }}
          onClick={() => setPurchaseSuccess(null)}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'white',
            borderRadius: '20px',
            padding: '32px',
            maxWidth: '400px',
            zIndex: 10001,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '64px', marginBottom: '16px' }}>🎉</div>
              <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '700', color: '#0f172a' }}>
                Purchase Successful!
              </h3>
              <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#64748b' }}>
                You purchased: <strong>{purchaseSuccess.item}</strong>
              </p>
              <div style={{
                background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
                border: '1.5px solid #10b981',
                borderRadius: '12px',
                padding: '16px 20px',
                marginBottom: '20px'
              }}>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  color: '#065f46',
                  lineHeight: 1.5
                }}>
                  {purchaseSuccess.message}
                </p>
              </div>
              <button
                onClick={() => setPurchaseSuccess(null)}
                style={{
                  background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Got it!
              </button>
            </div>
          </div>
        </>
      )}

      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '16px'
      }}>
        <h3 style={{
          margin: 0,
          fontSize: '16px',
          fontWeight: '700',
          color: '#0f172a',
          letterSpacing: '-0.01em'
        }}>
          Token Shop
        </h3>
        <div style={{
          background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(6, 182, 212, 0.1))',
          border: '1px solid rgba(14, 165, 233, 0.3)',
          padding: '6px 12px',
          borderRadius: '10px',
          fontSize: '13px',
          fontWeight: '700',
          color: '#0ea5e9',
          display: 'flex',
          alignItems: 'center',
          gap: '4px'
        }}>
          <span>🪙</span> {tokenBalance.toLocaleString()}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '48px 24px',
          color: '#64748b'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '12px', animation: 'float 2s ease-in-out infinite' }}>🏪</div>
          <p style={{ margin: 0, fontSize: '14px' }}>Loading shop...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div style={{
          padding: '24px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <p style={{ margin: '0 0 12px 0', color: '#DC2626', fontSize: '14px' }}>{error}</p>
          <button
            onClick={() => window.location.reload()}
            style={{
              background: '#EF4444',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Category Filter Tabs */}
      {!loading && !error && catalog && (
        <>
          <div style={{
            display: 'flex',
            gap: '6px',
            marginBottom: '16px',
            overflowX: 'auto',
            paddingBottom: '4px',
            WebkitOverflowScrolling: 'touch'
          }}>
            <button
              onClick={() => setActiveCategory('all')}
              style={{
                padding: '6px 12px',
                border: 'none',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                background: activeCategory === 'all' ? 'linear-gradient(135deg, #0ea5e9, #06b6d4)' : 'rgba(226, 232, 240, 0.8)',
                color: activeCategory === 'all' ? 'white' : '#64748b',
                transition: 'all 0.2s ease'
              }}
            >
              All ({catalog.categories.reduce((sum, c) => sum + c.items.length, 0)})
            </button>
            {catalog.categories.map(cat => {
              const meta = categoryMeta[cat.id] || { icon: '📦', color: '#64748b' };
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  style={{
                    padding: '6px 12px',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    background: activeCategory === cat.id ? meta.color : 'rgba(226, 232, 240, 0.8)',
                    color: activeCategory === cat.id ? 'white' : '#64748b',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <span>{meta.icon}</span> {meta.name} ({cat.items.length})
                </button>
              );
            })}
          </div>

          {/* Featured Item Banner */}
          {catalog.featured && activeCategory === 'all' && (
            <div style={{
              background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1), rgba(139, 92, 246, 0.1))',
              border: '1px solid rgba(236, 72, 153, 0.3)',
              borderRadius: '12px',
              padding: '12px 16px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '24px' }}>⭐</span>
              <div>
                <p style={{ margin: 0, fontSize: '13px', fontWeight: '700', color: '#EC4899' }}>
                  Featured: {catalog.featured.name}
                </p>
                <p style={{ margin: 0, fontSize: '11px', color: '#64748b' }}>
                  {catalog.featured.reason}
                </p>
              </div>
            </div>
          )}

          {/* Item Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
            gap: '12px',
            marginBottom: '24px'
          }}>
            {getDisplayItems().map(item => {
              const canAfford = tokenBalance >= item.price;
              const itemRarity = getRarity(item);
              const rarity = rarities[itemRarity];

          return (
            <div
              key={item.id}
              style={{
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(10px)',
                border: `2px solid ${rarity.color}`,
                borderRadius: '12px',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                opacity: canAfford ? 1 : 0.5,
                position: 'relative',
                boxShadow: canAfford ? `0 4px 20px ${rarity.glow}, 0 0 30px ${rarity.glow}` : 'none',
                cursor: canAfford ? 'pointer' : 'not-allowed'
              }}
              onMouseEnter={(e) => {
                if (canAfford) {
                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  e.currentTarget.style.boxShadow = `0 8px 30px ${rarity.glow}, 0 0 40px ${rarity.glow}`;
                }
              }}
              onMouseLeave={(e) => {
                if (canAfford) {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = `0 4px 20px ${rarity.glow}, 0 0 30px ${rarity.glow}`;
                }
              }}
            >
              {/* Minecraft Rarity Badge */}
              <div style={{
                position: 'absolute',
                top: '8px',
                left: '8px',
                background: rarity.color,
                color: 'white',
                padding: '3px 8px',
                borderRadius: '4px',
                fontSize: '9px',
                fontWeight: '700',
                letterSpacing: '0.5px',
                zIndex: 10,
                boxShadow: `0 2px 8px ${rarity.glow}`,
                border: '1px solid rgba(255, 255, 255, 0.3)',
                textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)'
              }}>
                {rarity.name}
              </div>

              {/* Consumable Badge */}
              {item.consumable && (
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'rgba(6, 182, 212, 0.9)',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '8px',
                  fontWeight: '700',
                  zIndex: 10
                }}>
                  CONSUMABLE
                </div>
              )}

              {/* Best Value Badge */}
              {item.best_value && (
                <div style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  fontSize: '8px',
                  fontWeight: '700',
                  zIndex: 10
                }}>
                  BEST VALUE
                </div>
              )}

              {/* Icon Header */}
              <div style={{
                background: getItemGradient(item),
                padding: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}>
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.15) 0%, transparent 60%)'
                }} />
                <div style={{
                  fontSize: '40px',
                  position: 'relative',
                  filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.2))'
                }}>
                  {getItemIcon(item)}
                </div>
              </div>

              {/* Info */}
              <div style={{ padding: '12px' }}>
                <h4 style={{
                  margin: '0 0 4px 0',
                  fontSize: '13px',
                  fontWeight: '700',
                  color: '#0f172a'
                }}>
                  {item.name}
                </h4>
                {item.description && (
                  <p style={{
                    margin: '0 0 8px 0',
                    fontSize: '11px',
                    color: '#64748b',
                    lineHeight: 1.4
                  }}>
                    {item.description}
                  </p>
                )}

                {/* Purchase Button */}
                <button
                  disabled={!canAfford || purchasing}
                  onClick={() => handlePurchase(item)}
                  style={{
                    width: '100%',
                    background: (canAfford && !purchasing) ?
                      'linear-gradient(135deg, #10b981, #059669)' :
                      'rgba(203, 213, 225, 0.5)',
                    border: 'none',
                    color: (canAfford && !purchasing) ? 'white' : '#94a3b8',
                    padding: '8px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '700',
                    cursor: (canAfford && !purchasing) ? 'pointer' : 'not-allowed',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px'
                  }}
                >
                  {purchasing ? (
                    'Processing...'
                  ) : canAfford ? (
                    <>
                      <span>🪙</span> {item.price}
                    </>
                  ) : (
                    `Need ${item.price - tokenBalance} more`
                  )}
                </button>
              </div>
            </div>
            );
          })}
          </div>

          {/* Coming Soon */}
          <div style={{
            padding: '16px',
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05), rgba(99, 102, 241, 0.05))',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>✨</div>
            <h4 style={{
              margin: '0 0 4px 0',
              fontSize: '13px',
              fontWeight: '700',
              color: '#6366f1'
            }}>
              More Coming Soon!
            </h4>
            <p style={{
              margin: 0,
              fontSize: '11px',
              color: '#64748b'
            }}>
              Session flair, limited-time items, gift system
            </p>
          </div>
        </>
      )}
    </div>
  );
};

// Earn Tab Component - 2025 Design
const EarnTab = ({ userTier }) => (
  <div>
    <h3 style={{
      margin: '0 0 20px 0',
      fontSize: '16px',
      fontWeight: '700',
      color: '#0f172a',
      letterSpacing: '-0.01em'
    }}>
      Ways to Earn Tokens
    </h3>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {[
        {
          icon: '💬',
          title: 'Ask Questions',
          description: 'Earn tokens for every question you ask during study sessions',
          reward: '5-15 tokens each',
          available: true,
          color: '#0ea5e9'
        },
        {
          icon: '🔥',
          title: 'Flow State Bonus',
          description: 'Triple your tokens when you\'re in the zone learning',
          reward: '3× multiplier',
          available: true,
          color: '#f59e0b'
        },
        {
          icon: '👥',
          title: 'Invite Friends',
          description: 'Earn tokens when friends join your study session',
          reward: '50 tokens',
          available: true,
          color: '#10b981'
        },
        {
          icon: '🎯',
          title: 'Complete Achievements',
          description: 'Learning streaks, breakthroughs, and milestones',
          reward: '10-100 tokens',
          available: true,
          color: '#059669'
        },
        {
          icon: '🏆',
          title: 'Bounty Challenges',
          description: 'Complete collaborative learning challenges',
          reward: '100-500 tokens',
          available: userTier !== 'guest',
          color: '#f59e0b'
        },
        // Family-specific earning methods for Home users
        ...(userTier === 'home' ? [
          {
            icon: '👨‍👩‍👧‍👦',
            title: 'Sibling Collaboration',
            description: 'Earn bonus tokens when family members help each other',
            reward: '25% bonus',
            available: true,
            isFamily: true,
            color: '#6366f1'
          },
          {
            icon: '🏠',
            title: 'Family Challenges',
            description: 'Complete weekly family learning challenges together',
            reward: '200-1000 tokens',
            available: true,
            isFamily: true,
            color: '#6366f1'
          }
        ] : [])
      ].map((method, index) => (
        <div key={index} style={{
          padding: '16px',
          background: method.available ?
            `linear-gradient(135deg, ${method.color}08, ${method.color}05)` :
            'rgba(248, 250, 252, 0.5)',
          backdropFilter: 'blur(10px)',
          borderRadius: '14px',
          border: method.available ?
            `1px solid ${method.color}30` :
            '1px solid rgba(203, 213, 225, 0.5)',
          opacity: method.available ? 1 : 0.5,
          transition: 'all 0.2s ease',
          cursor: method.available ? 'pointer' : 'default'
        }}
        onMouseEnter={(e) => {
          if (method.available) {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = `0 6px 20px ${method.color}20`;
            e.currentTarget.style.borderColor = `${method.color}50`;
          }
        }}
        onMouseLeave={(e) => {
          if (method.available) {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
            e.currentTarget.style.borderColor = `${method.color}30`;
          }
        }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '14px'
          }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '12px',
              background: method.available ?
                `linear-gradient(135deg, ${method.color}, ${method.color}dd)` :
                'rgba(203, 213, 225, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
              flexShrink: 0,
              boxShadow: method.available ? `0 2px 8px ${method.color}30` : 'none'
            }}>
              {method.icon}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '6px'
              }}>
                <div style={{
                  fontWeight: '600',
                  color: method.available ? '#0f172a' : '#94a3b8',
                  fontSize: '14px'
                }}>
                  {method.title}
                </div>
                {!method.available && (
                  <span style={{
                    fontSize: '10px',
                    fontWeight: '600',
                    color: '#64748b',
                    background: 'rgba(203, 213, 225, 0.5)',
                    padding: '2px 8px',
                    borderRadius: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px'
                  }}>
                    Premium
                  </span>
                )}
              </div>
              <div style={{
                fontSize: '13px',
                color: method.available ? '#64748b' : '#94a3b8',
                lineHeight: 1.4,
                marginBottom: '8px'
              }}>
                {method.description}
              </div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                background: method.available ? `${method.color}15` : 'rgba(203, 213, 225, 0.3)',
                padding: '4px 10px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '600',
                color: method.available ? method.color : '#94a3b8'
              }}>
                🪙 {method.reward}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Inventory Tab Component - Minecraft Loot Collection Style
const InventoryTab = ({ tokenData, userTier, onEquipItem }) => {
  const [activeCategory, setActiveCategory] = useState('all');

  // All possible items organized by category
  const itemCategories = {
    mira_colors: {
      name: 'Mira Colors',
      icon: '🎨',
      items: [
        { id: 10, name: 'Coral Mira', icon: '🔴', rarity: 'uncommon', type: 'mira_orb_color', value: 'coral' },
        { id: 11, name: 'Ocean Mira', icon: '🔵', rarity: 'uncommon', type: 'mira_orb_color', value: 'ocean' },
        { id: 12, name: 'Sunset Mira', icon: '🟠', rarity: 'rare', type: 'mira_orb_color', value: 'sunset' },
        { id: 13, name: 'Midnight Mira', icon: '🟣', rarity: 'rare', type: 'mira_orb_color', value: 'midnight' },
        { id: 14, name: 'Forest Mira', icon: '🟢', rarity: 'uncommon', type: 'mira_orb_color', value: 'forest' },
        { id: 15, name: 'Lavender Mira', icon: '💜', rarity: 'rare', type: 'mira_orb_color', value: 'lavender' },
      ]
    },
    mira_effects: {
      name: 'Mira Glow',
      icon: '✨',
      items: [
        { id: 20, name: 'Pulse Glow', icon: '💫', rarity: 'rare', type: 'mira_glow_effect', value: 'pulse' },
        { id: 21, name: 'Sparkle Glow', icon: '✨', rarity: 'rare', type: 'mira_glow_effect', value: 'sparkle' },
        { id: 22, name: 'Aurora Glow', icon: '🌈', rarity: 'epic', type: 'mira_glow_effect', value: 'aurora' },
        { id: 23, name: 'Rainbow Glow', icon: '🌟', rarity: 'legendary', type: 'mira_glow_effect', value: 'rainbow' },
      ]
    },
    mira_eyes: {
      name: 'Mira Eyes',
      icon: '👁️',
      items: [
        { id: 30, name: 'Starry Eyes', icon: '⭐', rarity: 'common', type: 'mira_eye_style', value: 'starry' },
        { id: 31, name: 'Happy Eyes', icon: '😊', rarity: 'common', type: 'mira_eye_style', value: 'happy' },
        { id: 32, name: 'Sleepy Eyes', icon: '😴', rarity: 'uncommon', type: 'mira_eye_style', value: 'sleepy' },
        { id: 33, name: 'Confident Eyes', icon: '😎', rarity: 'rare', type: 'mira_eye_style', value: 'confident' },
      ]
    },
    streak_protection: {
      name: 'Streak Protection',
      icon: '🛡️',
      items: [
        { id: 40, name: 'Streak Freeze', icon: '🧊', rarity: 'consumable', type: 'streak_freeze', value: 'owned', consumable: true },
        { id: 42, name: 'Weekend Amulet', icon: '🛡️', rarity: 'epic', type: 'weekend_amulet', value: 'active' },
      ]
    },
    profile: {
      name: 'Profile',
      icon: '🎭',
      items: [
        { id: 1, name: 'Basic Frame', icon: '🖼️', rarity: 'common', type: 'profile_border', value: 'basic' },
        { id: 5, name: 'Lightning Border', icon: '⚡', rarity: 'rare', type: 'profile_border', value: 'lightning' },
        { id: 2, name: 'Gold Username', icon: '✨', rarity: 'uncommon', type: 'username_color', value: 'gold' },
        { id: 3, name: 'Galaxy Theme', icon: '🌌', rarity: 'rare', type: 'profile_theme', value: 'galaxy' },
        { id: 4, name: 'Fire Effect', icon: '🔥', rarity: 'epic', type: 'profile_effect', value: 'fire' },
        { id: 50, name: 'Ocean Wave', icon: '🌊', rarity: 'rare', type: 'profile_effect', value: 'ocean_wave' },
        { id: 51, name: 'Sparkle', icon: '✨', rarity: 'rare', type: 'profile_effect', value: 'sparkle' },
        { id: 52, name: 'Nature', icon: '🌿', rarity: 'rare', type: 'profile_effect', value: 'nature' },
        { id: 6, name: 'Dragon Aura', icon: '🐉', rarity: 'legendary', type: 'profile_effect', value: 'dragon' },
      ]
    }
  };

  // Flatten all items
  const allItems = Object.values(itemCategories).flatMap(cat => cat.items);

  // Minecraft rarity colors + special tiers
  const rarities = {
    common: { color: '#9CA3AF', name: 'COMMON', glow: 'rgba(156, 163, 175, 0.4)' },
    uncommon: { color: '#10B981', name: 'UNCOMMON', glow: 'rgba(16, 185, 129, 0.5)' },
    rare: { color: '#3B82F6', name: 'RARE', glow: 'rgba(59, 130, 246, 0.6)' },
    epic: { color: '#A855F7', name: 'EPIC', glow: 'rgba(168, 85, 247, 0.7)' },
    legendary: { color: '#F59E0B', name: 'LEGENDARY', glow: 'rgba(245, 158, 11, 0.9)' },
    consumable: { color: '#06B6D4', name: 'CONSUMABLE', glow: 'rgba(6, 182, 212, 0.6)' },
    achievement: { color: '#EC4899', name: 'ACHIEVEMENT', glow: 'rgba(236, 72, 153, 1.0)' }
  };

  // Check if an item is owned
  const isItemOwned = (item) => {
    // Profile cosmetics
    if (item.type === 'profile_theme') return tokenData.profileTheme === item.value;
    if (item.type === 'username_color') return tokenData.usernameColor === item.value;
    if (item.type === 'profile_border') return tokenData.profileBorder === item.value;
    if (item.type === 'profile_effect') return tokenData.profileEffect === item.value;
    // Mira customization (owned if not 'default')
    if (item.type === 'mira_orb_color') return tokenData.miraOrbColor === item.value;
    if (item.type === 'mira_glow_effect') return tokenData.miraGlowEffect === item.value;
    if (item.type === 'mira_eye_style') return tokenData.miraEyeStyle === item.value;
    // Streak protection (consumables)
    if (item.type === 'streak_freeze') return (tokenData.streakFreezesOwned || 0) > 0;
    if (item.type === 'weekend_amulet') return tokenData.weekendAmuletActive === true;
    return false;
  };

  // Check if an item is currently equipped/active
  const isItemEquipped = (item) => {
    if (item.type === 'profile_theme') return tokenData.equippedProfileTheme === item.value;
    if (item.type === 'profile_border') return tokenData.equippedProfileBorder === item.value;
    if (item.type === 'profile_effect') return tokenData.equippedProfileEffect === item.value;
    // Mira items are always "equipped" when owned (immediate effect)
    if (item.type === 'mira_orb_color') return tokenData.miraOrbColor === item.value;
    if (item.type === 'mira_glow_effect') return tokenData.miraGlowEffect === item.value;
    if (item.type === 'mira_eye_style') return tokenData.miraEyeStyle === item.value;
    // Streak protection
    if (item.type === 'weekend_amulet') return tokenData.weekendAmuletActive === true;
    return false;
  };

  // Get quantity for consumable items
  const getItemQuantity = (item) => {
    if (item.type === 'streak_freeze') return tokenData.streakFreezesOwned || 0;
    return null;
  };

  // Handle clicking an item to equip/unequip
  const handleItemClick = (item, isOwned, isEquipped) => {
    if (!isOwned || item.consumable) return; // Can't equip consumables

    const action = isEquipped ? 'unequip' : 'equip';
    const value = isEquipped ? null : item.value;
    onEquipItem(item.type, value, action);
  };

  // Get items to display based on active category
  const getDisplayItems = () => {
    if (activeCategory === 'all') return allItems;
    return itemCategories[activeCategory]?.items || [];
  };

  // Count owned items
  const ownedItems = allItems.filter(item => isItemOwned(item));
  const collectionProgress = ownedItems.length;
  const totalItems = allItems.length;
  const progressPercent = (collectionProgress / totalItems) * 100;

  return (
    <div>
      {/* Collection Progress Header */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
        border: '2px solid rgba(99, 102, 241, 0.3)',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '20px',
        position: 'relative',
        overflow: 'hidden'
      }}>
        <div style={{
          fontSize: '14px',
          fontWeight: '700',
          color: '#6366f1',
          marginBottom: '12px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '20px' }}>🎒</span>
          Collection Progress
        </div>

        {/* Progress Bar */}
        <div style={{
          background: 'rgba(226, 232, 240, 0.5)',
          borderRadius: '12px',
          height: '12px',
          overflow: 'hidden',
          marginBottom: '8px',
          position: 'relative'
        }}>
          <div style={{
            background: 'linear-gradient(90deg, #0ea5e9, #10b981)',
            height: '100%',
            width: `${progressPercent}%`,
            borderRadius: '12px',
            transition: 'width 0.5s ease',
            boxShadow: '0 0 10px rgba(14, 165, 233, 0.5)'
          }} />
        </div>

        <div style={{
          fontSize: '13px',
          fontWeight: '600',
          color: '#64748b',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>{collectionProgress} / {totalItems} Items Collected</span>
          <span style={{ color: '#6366f1' }}>{Math.round(progressPercent)}%</span>
        </div>
      </div>

      {/* Category Filter Tabs */}
      <div style={{
        display: 'flex',
        gap: '6px',
        marginBottom: '16px',
        overflowX: 'auto',
        paddingBottom: '4px',
        WebkitOverflowScrolling: 'touch'
      }}>
        <button
          onClick={() => setActiveCategory('all')}
          style={{
            padding: '6px 10px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '11px',
            fontWeight: '600',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            background: activeCategory === 'all' ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(226, 232, 240, 0.8)',
            color: activeCategory === 'all' ? 'white' : '#64748b',
            transition: 'all 0.2s ease'
          }}
        >
          All ({allItems.length})
        </button>
        {Object.entries(itemCategories).map(([key, cat]) => (
          <button
            key={key}
            onClick={() => setActiveCategory(key)}
            style={{
              padding: '6px 10px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              background: activeCategory === key ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(226, 232, 240, 0.8)',
              color: activeCategory === key ? 'white' : '#64748b',
              transition: 'all 0.2s ease'
            }}
          >
            <span>{cat.icon}</span> {cat.name}
          </button>
        ))}
      </div>

      {/* Minecraft-Style Inventory Grid */}
      <div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '10px'
        }}>
          {getDisplayItems().map(item => {
            const isOwned = isItemOwned(item);
            const isEquipped = isItemEquipped(item);
            const rarity = rarities[item.rarity];
            const quantity = getItemQuantity(item);

            // Mira items and consumables can't be equipped/unequipped by clicking
            const canEquip = isOwned && !item.consumable && !item.type.startsWith('mira_');
            const isMiraItem = item.type.startsWith('mira_');

            return (
              <div
                key={item.id}
                style={{
                  background: isOwned ? 'rgba(255, 255, 255, 0.9)' : 'rgba(248, 250, 252, 0.5)',
                  border: `2px solid ${isOwned ? rarity.color : 'rgba(203, 213, 225, 0.4)'}`,
                  borderRadius: '10px',
                  padding: '12px',
                  textAlign: 'center',
                  position: 'relative',
                  transition: 'all 0.2s ease',
                  boxShadow: isOwned ? (isEquipped ? `0 6px 24px ${rarity.glow}` : `0 3px 12px ${rarity.glow}`) : 'none',
                  opacity: isOwned ? 1 : 0.4,
                  cursor: canEquip ? 'pointer' : 'default'
                }}
                onClick={() => canEquip && handleItemClick(item, isOwned, isEquipped)}
                onMouseEnter={(e) => {
                  if (isOwned) {
                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isOwned) {
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  }
                }}
              >
                {/* Rarity Badge */}
                {isOwned && (
                  <div style={{
                    position: 'absolute',
                    top: '4px',
                    left: '4px',
                    background: rarity.color,
                    color: 'white',
                    padding: '2px 5px',
                    borderRadius: '4px',
                    fontSize: '7px',
                    fontWeight: '700',
                    letterSpacing: '0.3px'
                  }}>
                    {rarity.name}
                  </div>
                )}

                {/* Equipped/Active Badge */}
                {isEquipped && isOwned && (
                  <div style={{
                    position: 'absolute',
                    top: '4px',
                    right: '4px',
                    background: isMiraItem ? '#EC4899' : '#10B981',
                    color: 'white',
                    padding: '2px 5px',
                    borderRadius: '4px',
                    fontSize: '7px',
                    fontWeight: '700',
                    letterSpacing: '0.3px'
                  }}>
                    {isMiraItem ? 'ACTIVE' : 'EQUIPPED'}
                  </div>
                )}

                {/* Quantity Badge for Consumables */}
                {quantity !== null && isOwned && (
                  <div style={{
                    position: 'absolute',
                    bottom: '4px',
                    right: '4px',
                    background: '#0EA5E9',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '700'
                  }}>
                    x{quantity}
                  </div>
                )}

                {/* Item Icon */}
                <div style={{
                  fontSize: '32px',
                  marginBottom: '6px',
                  marginTop: isOwned ? '8px' : '0',
                  filter: isOwned ? 'none' : 'grayscale(100%)',
                  opacity: isOwned ? 1 : 0.3
                }}>
                  {item.icon}
                </div>

                {/* Item Name */}
                <div style={{
                  fontSize: '10px',
                  fontWeight: '600',
                  color: isOwned ? '#0f172a' : '#94a3b8',
                  lineHeight: 1.2
                }}>
                  {isOwned ? item.name : '???'}
                </div>

                {/* Locked Overlay */}
                {!isOwned && (
                  <div style={{
                    fontSize: '14px',
                    marginTop: '2px'
                  }}>
                    🔒
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Motivational Message */}
      {collectionProgress < totalItems && (
        <div style={{
          marginTop: '20px',
          padding: '16px',
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.05), rgba(249, 115, 22, 0.05))',
          border: '1px solid rgba(245, 158, 11, 0.2)',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>⭐</div>
          <div style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#f59e0b',
            marginBottom: '4px'
          }}>
            Keep Learning to Unlock More!
          </div>
          <div style={{
            fontSize: '12px',
            color: '#64748b'
          }}>
            Earn tokens and collect all {totalItems} items
          </div>
        </div>
      )}

      {/* Completion Celebration */}
      {collectionProgress === totalItems && (
        <div style={{
          marginTop: '20px',
          padding: '20px',
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(249, 115, 22, 0.1))',
          border: '2px solid rgba(245, 158, 11, 0.4)',
          borderRadius: '16px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>🎉🏆🎉</div>
          <div style={{
            fontSize: '16px',
            fontWeight: '700',
            color: '#f59e0b',
            marginBottom: '8px'
          }}>
            COLLECTION COMPLETE!
          </div>
          <div style={{
            fontSize: '13px',
            color: '#64748b'
          }}>
            You've collected all {totalItems} items! You're a true collector!
          </div>
        </div>
      )}
    </div>
  );
};

// Daily Quests Tab Component - Duolingo-style
const DailyTab = ({ user, onTokensEarned }) => {
  const [quests, setQuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [timeUntilReset, setTimeUntilReset] = useState('');

  useEffect(() => {
    fetchQuests();

    // Update countdown every minute
    const timer = setInterval(() => {
      updateCountdown();
    }, 60000);
    updateCountdown();

    return () => clearInterval(timer);
  }, []);

  const updateCountdown = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    const diff = tomorrow - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    setTimeUntilReset(`${hours}h ${minutes}m`);
  };

  const fetchQuests = async () => {
    try {
      const response = await axios.get('/api/tokens/daily-quests');
      setQuests(response.data.quests || []);
    } catch (error) {
      console.error('Error fetching quests:', error);
    } finally {
      setLoading(false);
    }
  };

  const claimQuest = async (questId) => {
    setClaiming(questId);
    try {
      const response = await axios.post('/api/tokens/daily-quests/claim', {
        quest_id: questId
      });

      if (response.data.success) {
        // Refresh quests and token balance
        await fetchQuests();
        if (onTokensEarned) onTokensEarned();
      }
    } catch (error) {
      console.error('Error claiming quest:', error);
      alert(error.response?.data?.error || 'Failed to claim reward');
    } finally {
      setClaiming(null);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
        Loading quests...
      </div>
    );
  }

  const completedCount = quests.filter(q => q.claimed).length;
  const readyToClaim = quests.filter(q => q.progress >= q.target && !q.claimed).length;

  return (
    <div>
      {/* Header with timer */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <div>
          <h3 style={{
            margin: '0 0 4px 0',
            fontSize: '16px',
            fontWeight: '700',
            color: '#0f172a'
          }}>
            Daily Quests
          </h3>
          <div style={{
            fontSize: '12px',
            color: '#64748b'
          }}>
            {completedCount}/{quests.length} completed
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 12px',
          background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(6, 182, 212, 0.1))',
          borderRadius: '10px',
          border: '1px solid rgba(14, 165, 233, 0.2)'
        }}>
          <span style={{ fontSize: '14px' }}>⏰</span>
          <span style={{
            fontSize: '12px',
            fontWeight: '600',
            color: '#0ea5e9'
          }}>
            Resets in {timeUntilReset}
          </span>
        </div>
      </div>

      {/* Ready to claim banner */}
      {readyToClaim > 0 && (
        <div style={{
          padding: '12px 16px',
          background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(5, 150, 105, 0.1))',
          border: '1px solid rgba(16, 185, 129, 0.3)',
          borderRadius: '12px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px'
        }}>
          <span style={{ fontSize: '20px' }}>🎉</span>
          <span style={{
            fontSize: '13px',
            fontWeight: '600',
            color: '#059669'
          }}>
            {readyToClaim} quest{readyToClaim > 1 ? 's' : ''} ready to claim!
          </span>
        </div>
      )}

      {/* Quest Cards */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {quests.map((quest) => {
          const isComplete = quest.progress >= quest.target;
          const progressPercent = Math.min((quest.progress / quest.target) * 100, 100);

          return (
            <div
              key={quest.id}
              style={{
                padding: '16px',
                background: quest.claimed
                  ? 'rgba(226, 232, 240, 0.3)'
                  : isComplete
                    ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(5, 150, 105, 0.05))'
                    : 'white',
                borderRadius: '14px',
                border: quest.claimed
                  ? '1px solid rgba(203, 213, 225, 0.5)'
                  : isComplete
                    ? '1px solid rgba(16, 185, 129, 0.3)'
                    : '1px solid rgba(226, 232, 240, 0.8)',
                boxShadow: quest.claimed
                  ? 'none'
                  : '0 2px 8px rgba(0, 0, 0, 0.04)',
                opacity: quest.claimed ? 0.6 : 1,
                transition: 'all 0.2s ease'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '14px'
              }}>
                {/* Quest Icon */}
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: quest.claimed
                    ? 'rgba(203, 213, 225, 0.5)'
                    : isComplete
                      ? 'linear-gradient(135deg, #10b981, #059669)'
                      : 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  flexShrink: 0,
                  boxShadow: isComplete && !quest.claimed
                    ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                    : 'none'
                }}>
                  {quest.claimed ? '✓' : quest.icon}
                </div>

                {/* Quest Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '6px'
                  }}>
                    <div>
                      <div style={{
                        fontWeight: '600',
                        fontSize: '14px',
                        color: quest.claimed ? '#94a3b8' : '#0f172a',
                        marginBottom: '2px'
                      }}>
                        {quest.title}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#64748b'
                      }}>
                        {quest.description}
                      </div>
                    </div>

                    {/* Reward Badge */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '4px 8px',
                      background: quest.claimed
                        ? 'rgba(203, 213, 225, 0.3)'
                        : 'linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(6, 182, 212, 0.1))',
                      borderRadius: '8px',
                      flexShrink: 0
                    }}>
                      <span style={{ fontSize: '12px' }}>💎</span>
                      <span style={{
                        fontSize: '12px',
                        fontWeight: '700',
                        color: quest.claimed ? '#94a3b8' : '#0ea5e9'
                      }}>
                        {quest.reward}
                      </span>
                    </div>
                  </div>

                  {/* Progress Section */}
                  <div style={{ marginTop: '10px' }}>
                    {/* Progress Bar */}
                    <div style={{
                      background: 'rgba(226, 232, 240, 0.8)',
                      borderRadius: '6px',
                      height: '8px',
                      overflow: 'hidden',
                      marginBottom: '6px'
                    }}>
                      <div style={{
                        background: quest.claimed
                          ? '#94a3b8'
                          : isComplete
                            ? 'linear-gradient(90deg, #10b981, #059669)'
                            : 'linear-gradient(90deg, #0ea5e9, #06b6d4)',
                        height: '100%',
                        width: `${progressPercent}%`,
                        borderRadius: '6px',
                        transition: 'width 0.3s ease'
                      }} />
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      {/* Progress Text */}
                      <span style={{
                        fontSize: '11px',
                        fontWeight: '600',
                        color: quest.claimed ? '#94a3b8' : isComplete ? '#059669' : '#64748b'
                      }}>
                        {quest.progress}/{quest.target}
                      </span>

                      {/* Claim Button */}
                      {isComplete && !quest.claimed && (
                        <button
                          onClick={() => claimQuest(quest.id)}
                          disabled={claiming === quest.id}
                          style={{
                            padding: '6px 14px',
                            background: 'linear-gradient(135deg, #10b981, #059669)',
                            border: 'none',
                            borderRadius: '8px',
                            color: 'white',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: claiming === quest.id ? 'wait' : 'pointer',
                            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                            transition: 'all 0.2s ease',
                            opacity: claiming === quest.id ? 0.7 : 1
                          }}
                          onMouseEnter={(e) => {
                            if (claiming !== quest.id) {
                              e.currentTarget.style.transform = 'scale(1.05)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.3)';
                          }}
                        >
                          {claiming === quest.id ? 'Claiming...' : 'Claim'}
                        </button>
                      )}

                      {quest.claimed && (
                        <span style={{
                          fontSize: '11px',
                          fontWeight: '600',
                          color: '#10b981',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          ✓ Claimed
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* All Complete Message */}
      {completedCount === quests.length && quests.length > 0 && (
        <div style={{
          marginTop: '20px',
          padding: '20px',
          background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(249, 115, 22, 0.1))',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: '14px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>🎉</div>
          <div style={{
            fontSize: '14px',
            fontWeight: '700',
            color: '#f59e0b',
            marginBottom: '4px'
          }}>
            All Quests Complete!
          </div>
          <div style={{
            fontSize: '12px',
            color: '#64748b'
          }}>
            Come back tomorrow for new quests
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenWalletModal;